import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { ValidationErrors } from '../../../lib/models/validation/validation-errors';
import { FormApplication } from '../../shared/models/form-application.model';
import { MortgageClubsDto } from '../../shared/models/mortgage-clubs-dto.model';
import { ApplicantEmployment } from '../models/applicant-employment.model';
import { Applicant } from '../models/applicant.model';
import { DeclarationPageContentDto } from '../models/declaration-page-content-dto';
import { EditableAppFlagsDto } from '../models/editable-app-flags-dto.model';
import { MortgageApplicationResponse } from '../models/mortgage-application-response.model';
import { MortgageApplication } from '../models/mortgage-application.model';
import { Address } from './../../../lib/models/api/address.model';
import { Associate } from './../../../lib/models/api/associate.model';
import { AffordabilityResultsDto } from './../../../lib/models/dto/affordability-results-dto.model';
import { EntityResponseDto } from './../../../lib/models/dto/entity-response-dto.model';
import { MortgageApplicationStore } from './mortgage-application-store';

@Injectable()
export class MortgageApplicationService {
  constructor(
    protected readonly httpClient: HttpClient,
    protected readonly mortgageApplicationStore: MortgageApplicationStore
  ) {}

  protected static getMortgageApplicationResponse(data: any): MortgageApplicationResponse {
    const mortgageApplicationResponse = new MortgageApplicationResponse();
    mortgageApplicationResponse.mortgageApplicationEntity = new MortgageApplication().deserialize(data.Entity);
    mortgageApplicationResponse.sections = data.Sections;
    mortgageApplicationResponse.obsoleteFieldNotifications = data.ObsoleteFieldNotifications;
    mortgageApplicationResponse.validationErrors = new ValidationErrors(data.ValidationErrors);

    return mortgageApplicationResponse;
  }

  public loadMortgageApplication(mortgageApplicationId: string): Observable<MortgageApplicationResponse> {
    const params = new HttpParams().set('id', mortgageApplicationId);

    return this.httpClient.get('/Applications/GetMortgageApplication', { params }).pipe(
      map((data: any) => {
        return MortgageApplicationService.getMortgageApplicationResponse(data);
      })
    );
  }

  public saveMortgageApplication(mortgageApplication: MortgageApplication): Observable<MortgageApplicationResponse> {
    return this.httpClient.put('/Applications/UpdateMortgageApplication', mortgageApplication).pipe(
      map((data: any) => {
        return MortgageApplicationService.getMortgageApplicationResponse(data);
      })
    );
  }

  public saveFactFind2(mortgageApplication: MortgageApplication): Observable<MortgageApplicationResponse> {
    return this.httpClient.put('/AdviceCaseManagement/SaveFactFind2', mortgageApplication).pipe(
      map((data: any) => {
        return MortgageApplicationService.getMortgageApplicationResponse(data);
      })
    );
  }

  public saveFactFind1(mortgageApplication: MortgageApplication): Observable<MortgageApplicationResponse> {
    return this.httpClient.put('/AdviceCaseManagement/SubmitAdvice', mortgageApplication).pipe(
      map((data: any) => {
        return MortgageApplicationService.getMortgageApplicationResponse(data);
      })
    );
  }

  public loadAvailableLoanTypes(mortgageApplication: FormApplication): Observable<any> {
    const params = new HttpParams()
      .set('caseNumber', mortgageApplication.ApplicationCase.CaseNumberAsHash)
      .append('loanGroupTypeCode', mortgageApplication.ApplicationCase.LoanGroupTypeCode)
      .append('IsKfi', 'false')
      .append('IsAip', mortgageApplication.IsAip.toString())
      .append('IsFullApp', mortgageApplication.IsFullApp.toString())
      .append('propositionId', mortgageApplication.ApplicationCase.PropositionIDCache.toString());

    return this.httpClient.get('/ApplicationCase/GetLoanTypesByLoanGroupTypeCode', { params }).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  public getSolicitors(postcode: string): Observable<Array<Associate>> {
    return this.httpClient.get(`/Solicitors/getSolicitors?postcode=${postcode}`).pipe(
      map((data: any) => {
        return (data as Array<any>).map((item) => new Associate().deserialize(item));
      })
    );
  }

  public getDeclarationPageContent(mortgageApplication: MortgageApplication): Observable<DeclarationPageContentDto> {
    const params = new HttpParams()
      .set('propositionId', mortgageApplication.ApplicationCase.PropositionIDCache.toString())
      .append('interfaceTypeId', mortgageApplication.ApplicationCase.InterfaceTypeIDCache.toString());

    return this.httpClient.get('/configuration/declarationPageContent', { params }).pipe(
      map((data: any) => {
        return data as DeclarationPageContentDto;
      })
    );
  }

  public getPreviousAddresses(mortgageApplication: MortgageApplication): Observable<Array<Address>> {
    return this.httpClient.put('/Applications/retrieveAddresses', mortgageApplication).pipe(
      map((data: any) => {
        return (data as Array<any>).map((item) => new Address().deserialize(item));
      })
    );
  }

  public getAddresses(postcode: string): Observable<Array<Address>> {
    return this.httpClient.get<Array<Address>>('/addresses/getAddresses', { params: { postcode } }).pipe(
      map((data: any) => {
        return (data as Array<any>).map((item) => new Address().deserialize(item));
      })
    );
  }

  public isEditableApplication(applicationId: string): Observable<EditableAppFlagsDto> {
    return this.httpClient
      .get<EditableAppFlagsDto>('/Applications/IsEditableApplication', {
        params: {
          applicationId
        }
      })
      .pipe(
        map((response) => {
          return new EditableAppFlagsDto().deserialize(response);
        })
      );
  }

  public getMortgageClubs(application: FormApplication): Observable<MortgageClubsDto> {
    const params = new HttpParams().set('agentId', application.ApplicationCase.AgentID.toString());

    return this.httpClient.get('/InternalAgents/mortgageClubs', { params }).pipe(
      map((data: any) => {
        return data as MortgageClubsDto;
      })
    );
  }

  public validateMortgageApplication(
    mortgageApplication: MortgageApplication
  ): Observable<MortgageApplicationResponse> {
    return this.httpClient.put('/Applications/Validate', mortgageApplication).pipe(
      map((mortgageApplicationResponse: any) => {
        return MortgageApplicationService.getMortgageApplicationResponse(mortgageApplicationResponse);
      }),
      switchMap((validatedMortgageApplication: MortgageApplicationResponse) =>
        this.attemptToSave(validatedMortgageApplication)
      )
    );
  }

  public checkIfAnyKfiForCalculatingFees(applicationId: string): Observable<boolean> {
    const params = new HttpParams().set('applicationId', applicationId);

    return this.httpClient.get('/Applications/CheckIfAnyKfiForCalculatingFees', { params }).pipe(
      map((data: any) => {
        return data && data.IsAnyKfiForCalculatingFees;
      })
    );
  }

  public performAdhocAffordabilityCheck(
    mortgageApplication: MortgageApplication
  ): Observable<EntityResponseDto<AffordabilityResultsDto>> {
    return this.httpClient
      .put<EntityResponseDto<AffordabilityResultsDto>>(
        '/AdviceCaseManagement/PerformAdhocAffordabilityCheck',
        mortgageApplication
      )
      .pipe(
        map((response) => {
          const deserialized = new EntityResponseDto<AffordabilityResultsDto>().deserialize(response);
          deserialized.Entity = new AffordabilityResultsDto().deserialize(response.Entity);

          return deserialized;
        })
      );
  }

  public submitMortgageApplication(mortgageApplication: MortgageApplication): Observable<MortgageApplicationResponse> {
    return this.httpClient.put('/Applications/Submit', mortgageApplication).pipe(
      map((data: any) => {
        return MortgageApplicationService.getMortgageApplicationResponse(data);
      })
    );
  }

  public hasNoError(mortgageApplicationResponse): boolean {
    return mortgageApplicationResponse.validationErrors.FullValidationErrors.length === 1;
  }

  private attemptToSave(
    mortgageApplicationResponse: MortgageApplicationResponse
  ): Observable<MortgageApplicationResponse> {
    mortgageApplicationResponse.isSubmitted = true;
    this.mortgageApplicationStore.mortgageApplicationResponse = mortgageApplicationResponse;

    if (this.hasNoError(mortgageApplicationResponse)) {
      return this.saveMortgageApplication(
        this.mortgageApplicationStore.mortgageApplicationResponse.mortgageApplicationEntity
      ).pipe(
        tap((response) => {
          response.mortgageApplicationEntity.IsFactFindApplicantOrderChangeAfterSam =
            mortgageApplicationResponse.mortgageApplicationEntity.IsFactFindApplicantOrderChangeAfterSam;
        })
      );
    }

    mortgageApplicationResponse.hasSubmitValidationError = true;
    this.mortgageApplicationStore.mortgageApplicationResponse = mortgageApplicationResponse;

    return of(undefined);
  }

  public prepareDataForSubmit(mortgageApplication: MortgageApplication): void {
    this.removeEmptyApplicationAddresses(mortgageApplication);

    for (const applicant of mortgageApplication.Applicants) {
      this.removeEmptyApplicantAddresses(applicant);
    }

    this.addApplicationDefaultValues(mortgageApplication);
  }

  private removeEmptyApplicationAddresses(mortgageApplication: MortgageApplication): void {
    const properties: Array<keyof MortgageApplication> = [
      'PropertyAddress',
      'SolicitorAddress',
      'PackagerAddress',
      'PropertyAccessContactAddress',
      'PropertySellingEstateAgentAddress',
      'SolicitorConveyaneFirmAddress',
      'VendorAddress'
    ];

    this.setPropertiesUndefinedIfEmpty(mortgageApplication, properties);
  }

  private removeEmptyApplicantAddresses(applicant: Applicant): void {
    this.setPropertiesUndefinedIfEmpty(applicant, ['LenderAddress', 'FutureCorrespondingAddress', 'SolicitorAddress']);

    if (applicant.ExistingProperties) {
      for (const property of applicant.ExistingProperties) {
        this.setPropertiesUndefinedIfEmpty(property, ['Address']);
      }
    }

    if (applicant.ApplicantEmployments) {
      for (const employment of applicant.ApplicantEmployments) {
        this.removeEmptyEmploymentAddresses(employment);
      }
    }
  }

  private removeEmptyEmploymentAddresses(employment: ApplicantEmployment): void {
    this.setPropertiesUndefinedIfEmpty(employment, ['AccountantAddress', 'HRAddress', 'EmployerAddress']);
  }

  private setPropertiesUndefinedIfEmpty<T>(object: T, properties: Array<keyof T>): void {
    for (const property of properties) {
      if (this.hasValueAndIsEmptyObject(object[property])) {
        object[property] = undefined;
      }
    }
  }

  private hasValueAndIsEmptyObject(object: Object): boolean {
    return object && Object.keys(object).length === 0;
  }

  public prepareDataForSave(mortgageApplication: MortgageApplication): void {
    this.prepareDataForSubmit(mortgageApplication);

    for (const applicant of mortgageApplication.Applicants) {
      for (const employment of applicant.ApplicantEmployments) {
        this.updateHRAddressIfRequired(mortgageApplication, employment);
        this.removeApplicantBlankArrays(employment);
      }
    }
  }

  private updateHRAddressIfRequired(mortgageApplication: MortgageApplication, employment: ApplicantEmployment): void {
    if (this.shouldCopyHrAddressFromEmployerAddress(mortgageApplication, employment)) {
      if (!employment.HRAddress) {
        employment.HRAddress = new Address();
      }

      employment.HRAddress.copyAddressFields(employment.EmployerAddress);
    }
  }

  private removeApplicantBlankArrays(employment: ApplicantEmployment): void {
    if (!employment.ApplicantEmploymentAccountantTypes || employment.ApplicantEmploymentAccountantTypes.length === 0) {
      delete employment.ApplicantEmploymentAccountantTypes;
    }
    if (
      !employment.ApplicantEmploymentPrivatePensionSources ||
      employment.ApplicantEmploymentPrivatePensionSources.length === 0
    ) {
      delete employment.ApplicantEmploymentPrivatePensionSources;
    }
  }

  private shouldCopyHrAddressFromEmployerAddress(
    mortgageApplication: MortgageApplication,
    employment: ApplicantEmployment
  ): boolean {
    return (
      mortgageApplication.IsFullApp &&
      employment.isEmployed() &&
      employment.EmployerAddress !== undefined &&
      employment.EmployerAddress !== null &&
      !employment.HasDifferentHRAddress
    );
  }

  private addApplicationDefaultValues(application: MortgageApplication): void {
    const propertiesMap: Array<{ name: keyof MortgageApplication; value: any }> = [
      { name: 'SolicitorAddress', value: {} },
      { name: 'VendorAddress', value: {} }
    ];
    for (const propertyMap of propertiesMap) {
      if (!application[propertyMap.name]) {
        application[propertyMap.name] = propertyMap.value as never;
      }
    }

    if (application.ApplicationProducts && application.ApplicationProducts.length) {
      for (const key of Object.keys(application.ApplicationProducts[0].CaseProduct)) {
        if (application.ApplicationProducts[0].CaseProduct[key]) {
          return;
        }
      }

      application.ApplicationProducts = [];
    }
  }
}
